import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      toast.error('Keine Session-ID gefunden');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt >= maxAttempts) {
      setStatus('error');
      toast.error('Zahlungsstatus-Prüfung fehlgeschlagen');
      return;
    }

    try {
      const response = await axios.get(`/billing/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Zahlung erfolgreich!');
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/admin');
        }, 3000);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Zahlung abgelaufen');
      } else {
        // Continue polling
        setAttempts(attempt + 1);
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      toast.error('Fehler bei der Zahlungsüberprüfung');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-center text-white">
            {status === 'checking' && 'Zahlung wird geprüft...'}
            {status === 'success' && 'Zahlung erfolgreich!'}
            {status === 'error' && 'Zahlung fehlgeschlagen'}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-6">
          {status === 'checking' && (
            <>
              <Loader className="h-16 w-16 text-blue-400 animate-spin" />
              <p className="text-zinc-400 text-center">
                Ihre Zahlung wird überprüft. Bitte warten Sie einen Moment...
              </p>
              <p className="text-sm text-zinc-500">
                Versuch {attempts + 1} von {maxAttempts}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="h-16 w-16 rounded-full bg-green-900/30 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-400" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">
                  Vielen Dank für Ihre Zahlung!
                </h3>
                <p className="text-zinc-400">
                  Ihr Abonnement wurde erfolgreich aktiviert.
                </p>
                <p className="text-sm text-zinc-500 mt-2">
                  Sie werden in Kürze weitergeleitet...
                </p>
              </div>
              <Button onClick={() => navigate('/admin')} size="lg" className="w-full">
                Zum Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="h-16 w-16 rounded-full bg-red-900/30 flex items-center justify-center">
                <span className="text-4xl">❌</span>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">
                  Zahlung fehlgeschlagen
                </h3>
                <p className="text-zinc-400">
                  Es gab ein Problem mit Ihrer Zahlung. Bitte versuchen Sie es erneut.
                </p>
              </div>
              <Button onClick={() => navigate('/admin/billing')} size="lg" className="w-full">
                Zurück zur Abrechnung
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSuccess;
